import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

// Premium fashion/editorial signup modal.
// Upper visual = news-freefest-2026-04.jpg (Freeway on stage, black snapback +
// Snatch the Dishes tee). A black gradient rises from the bottom of the photo
// so the SIGN UP TODAY headline reads cleanly overlaid on the image. Copy
// preserves the current 10% signup flow — token minted server-side, applied
// to the shopping session, single-use once checkout consumes it.
//
// Session-scoped (fta_welcome_v3). No old-popup flash — the modal is the only
// popup component and appears exactly once per browser session after the
// intro finishes.
export default function WelcomePopup({ onClose }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const inputRef = useRef(null);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null); // null | 'loading' | 'ok' | 'error'
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    // preventScroll: true — avoids the mobile-browser jump-to-input that
    // otherwise scrolls the underlying page upward on popup mount.
    const t = setTimeout(() => inputRef.current?.focus?.({ preventScroll: true }), 60);
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; clearTimeout(t); document.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) { setStatus("error"); setErrMsg("Please enter a valid email."); return; }
    setStatus("loading");
    try {
      const res = await fetch(`${API}/api/subscribe`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "signup-popup" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Please try again.");
      if (data.discount_token) localStorage.setItem("fta_discount_v1", data.discount_token);
      setStatus("ok");
      setTimeout(() => onClose?.(), 1800);
    } catch (err) { setStatus("error"); setErrMsg(err.message || "Something went wrong."); }
  };

  return (
    <div
      data-testid="welcome-popup"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(88vw, 360px)" }}
        className="relative bg-white shadow-2xl overflow-hidden"
      >
        {/* Close */}
        <button
          onClick={onClose}
          data-testid="welcome-popup-close"
          aria-label="Close"
          className="absolute top-2 right-2 z-20 w-8 h-8 flex items-center justify-center bg-black/50 hover:bg-black text-white rounded-full transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        {/* Upper visual — news-freefest-2026-04.jpg, compact height */}
        <div className="relative w-full aspect-[5/4] bg-black overflow-hidden">
          <img
            src="/assets/news/freefest-2026/news-freefest-2026-04.jpg"
            alt=""
            data-testid="welcome-popup-image"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "center 20%" }}
          />
          {/* Rising black gradient anchoring the overlay copy */}
          <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black via-black/70 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 px-4 pb-3.5 text-white">
            <div className="font-mono text-[9px] tracking-[0.4em] uppercase text-white/70 mb-1">
              Philadelphia Freeway · FTA
            </div>
            <h2
              data-testid="welcome-popup-heading"
              className="font-wide uppercase text-3xl leading-[0.95] text-[#F4C10A]"
            >
              Get 10% Off
            </h2>
            <div
              data-testid="welcome-popup-subheading"
              className="mt-1 font-mono uppercase text-[11px] tracking-[0.32em] leading-tight text-white/85"
            >
              Join the Newsletter
            </div>
          </div>
        </div>

        {/* Lower — form */}
        <div className="px-4 pt-3.5 pb-4 bg-white text-black">
          {status === "ok" ? (
            <div data-testid="welcome-popup-success" className="bg-black text-white p-4 text-center">
              <div className="font-wide uppercase text-base">10% Off Unlocked</div>
              <div className="mt-0.5 font-mono text-[10px] tracking-[0.2em] text-white/70">
                Discount applied to your next order.
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-2.5">
              <input
                ref={inputRef}
                data-testid="welcome-popup-email"
                type="email"
                required
                autoComplete="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-black/25 focus:border-black outline-none px-3.5 py-2.5 text-sm text-black bg-white placeholder-black/40"
              />
              <button
                type="submit"
                data-testid="welcome-popup-cta"
                disabled={status === "loading"}
                className="w-full bg-black text-white font-display uppercase text-xs tracking-[0.22em] py-3 hover:bg-[#F4C10A] hover:text-black transition-colors disabled:opacity-50"
              >
                {status === "loading" ? "Submitting…" : "Subscribe & Save 10%"}
              </button>
              {status === "error" && (
                <div data-testid="welcome-popup-error" className="text-red-600 text-xs">{errMsg}</div>
              )}
            </form>
          )}
          <div className="mt-2.5 font-mono text-[9px] tracking-[0.2em] uppercase text-black/50 text-center">
            By subscribing you agree to our{" "}
            <Link to="/privacy" data-testid="welcome-popup-privacy" className="underline hover:text-black">
              Privacy Policy
            </Link>
            .
          </div>
        </div>
      </div>
    </div>
  );
}
